/**
 * How high hls.js's automatic quality selection may climb.
 *
 * A cap is an index into hls.js's level list, the value `hls.autoLevelCapping` takes. hls.js sorts
 * that list on height first (then frame rate, codec and bitrate), so within one stream an index
 * bound is a resolution bound, and the variants of one frame sit next to each other. `NO_CAP` is
 * hls.js's own spelling of "uncapped".
 *
 * Two caps, combined by keeping the lower:
 *
 * - **the eco cap** holds auto mode at 720p. Rungs are named by their *short* side, so a portrait
 *   video's 720p rung is 720×1280; comparing heights would hold that video at 360p.
 * - **the size cap** holds auto mode at the smallest frame that fills the video's rendered area in
 *   device pixels, so a small player does not fetch pixels it can only throw away when it scales
 *   the picture down.
 *
 * Neither bounds a quality the viewer picks: `autoLevelCapping` only bounds hls.js's own choice.
 */

/** A level's frame, as hls.js reports it: `0×0` when the variant declares no `RESOLUTION`. */
export interface LevelFrame {
  readonly width: number
  readonly height: number
}

/** The video element's CSS box. */
export interface PlayerBox {
  readonly width: number
  readonly height: number
}

/** hls.js's `autoLevelCapping` value for "no cap". */
export const NO_CAP = -1

/** Eco mode's ceiling, on the frame's short side: the 720p rung of a landscape or a portrait video. */
export const ECO_SHORT_SIDE = 720

/**
 * The highest device pixel ratio the size cap honours.
 *
 * Past it, the frame asked for follows the screen's density rather than the player's size: at a
 * phone's real ratio of 3, a fullscreen player 390 CSS pixels tall asks for 2160p; at 1.5, for
 * 720p. A desktop player 1280 CSS pixels wide on a ratio-2 screen asks for 2160p at 2, 1080p at 1.5.
 */
export const MAX_PIXEL_RATIO = 1.5

/** The measure a rung is named by: 720p is a short side of 720, whichever way the frame stands. */
export function shortSide(level: LevelFrame): number {
  return Math.min(level.width, level.height)
}

/** Whether eco mode would hold anything back: some level is above 720p. */
export function hasEcoHeadroom(levels: readonly LevelFrame[]): boolean {
  return levels.some((level) => shortSide(level) > ECO_SHORT_SIDE)
}

/**
 * The eco cap: the highest level whose short side is at most 720, or the lowest level when every
 * one is above it. A level with no declared frame counts as within it, there being nothing to judge
 * it by.
 */
export function ecoCap(levels: readonly LevelFrame[]): number {
  if (levels.length === 0) return NO_CAP
  let cap = 0
  levels.forEach((level, index) => {
    if (shortSide(level) <= ECO_SHORT_SIDE) cap = index
  })
  return cap
}

/**
 * The size cap: the smallest level whose frame covers the rendered video area in device pixels,
 * or the top level when none does.
 *
 * The rendered area is the box fitted to the stream's aspect ratio, as `object-fit: contain` draws
 * it — a 16:9 video in a 21:9 box fills its height, not its width. A box with no area caps nothing:
 * it is an element not laid out yet, and its first layout recomputes the cap. So does a stream
 * whose levels declare no frame.
 */
export function sizeCap(levels: readonly LevelFrame[], box: PlayerBox, pixelRatio: number): number {
  if (levels.length === 0 || !(box.width > 0) || !(box.height > 0)) return NO_CAP

  const aspect = streamAspect(levels)
  if (aspect === null) return NO_CAP

  // `object-fit: contain`: the side the box has in excess is left empty.
  let width = box.width
  let height = box.height
  if (width / height > aspect) width = height * aspect
  else height = width / aspect

  // A fraction of a device pixel is layout rounding, not a sharper picture to ask for.
  const ratio = Math.min(pixelRatio > 0 ? pixelRatio : 1, MAX_PIXEL_RATIO)
  const needWidth = Math.round(width * ratio)
  const needHeight = Math.round(height * ratio)

  const covering = levels.findIndex((level) => level.width >= needWidth && level.height >= needHeight)
  if (covering === -1) return levels.length - 1
  return lastOfFrame(levels, covering)
}

/** The lower of two caps, `NO_CAP` being the highest. */
export function lowerCap(a: number, b: number): number {
  if (a === NO_CAP) return b
  if (b === NO_CAP) return a
  return Math.min(a, b)
}

/**
 * The cap auto mode plays under: the size cap, lowered to the eco cap while eco mode is on.
 * A `box` of `null` leaves the size out, for a player that does not cap to its size.
 */
export function effectiveCap(
  levels: readonly LevelFrame[],
  options: { box: PlayerBox | null; pixelRatio: number; eco: boolean },
): number {
  const size = options.box ? sizeCap(levels, options.box, options.pixelRatio) : NO_CAP
  return options.eco ? lowerCap(size, ecoCap(levels)) : size
}

/** The stream's width over height, read off its largest declared frame; `null` when none is declared. */
function streamAspect(levels: readonly LevelFrame[]): number | null {
  let largest: LevelFrame | null = null
  for (const level of levels) {
    if (level.width > 0 && level.height > 0 && (!largest || level.height > largest.height)) largest = level
  }
  return largest ? largest.width / largest.height : null
}

/**
 * The last index carrying the same frame as `index`. A frame offered at several bitrates or codecs
 * is several levels in a row, and capping at the first would forbid the others.
 */
function lastOfFrame(levels: readonly LevelFrame[], index: number): number {
  const frame = levels[index]
  let last = index
  for (let next = index + 1; next < levels.length; next++) {
    const level = levels[next]
    if (!frame || !level || level.width !== frame.width || level.height !== frame.height) break
    last = next
  }
  return last
}
